import { useNavigation } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import fetch from 'cross-fetch';
import { Field, Formik } from 'formik';
import jwt_decode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { useTranslation } from 'react-i18next';
import {
  Linking,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
} from 'react-native';

import { InviteEmailVerificationStep } from '@components/Invite/InviteEmailVerificationStep.web';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import TextField from '@components/shared/TextField/TextField';
import { useGoogleAuth } from '@hooks/useGoogleAuth';
import { scopedRegisterSchema } from '@schemas/scopedRegisterSchema.web';
import { useAuthContext } from '@src/context/authContext';
import Images from '@themes/images';
import theme from '@themes/theme';
import { sendEmailVerificationCode } from '@utils/emailVerification';
import { getApiConfig } from '@utils/getApiConfig';
import { isMobileBrowser } from '@utils/mobileDeepLink';
import { parseRegisterResponse } from '@utils/parseRegisterResponse';
import { RATE_LIMIT_ERROR_KEY } from '@utils/rateLimit';
import { isSocialIdentityError } from '@utils/socialRegistration';
import {
  INVITE_SIGNUP_START_CONVERSATION_KEY,
  INVITE_SIGNUP_TASKS_WAITING_KEY,
} from '@utils/startConversationTutorial';
import { AsyncStorage, SecurityStorage } from '@utils/storage';

export type ScopedSignupModalProps = {
  invitationType: 'project' | 'team' | 'task';
  entityName: string;
  entityAddress?: string;
  projectName?: string;
  projectIcon?: string;
  inviterName: string;
  role: string;
  email: string;
  invitationToken: string;
  entityId?: string;
  isLinkInvite?: boolean;
  /**
   * Prefill `email` and make the field read-only. Defaults to `!isLinkInvite`;
   * set it explicitly when request mode and the email lock disagree (an
   * expired email invite: request to join, but with the invited address).
   */
  isEmailLocked?: boolean;
  isFullPage?: boolean;
  usersCount?: number | null;
  onClose: () => void;
  onSuccess: () => void;
};

type ScopedRegisterValues = {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
};

export const ScopedSignupModal: React.FC<ScopedSignupModalProps> = ({
  invitationType,
  entityName,
  entityAddress,
  projectName,
  projectIcon,
  inviterName,
  role,
  email,
  invitationToken,
  entityId,
  isLinkInvite = false,
  isEmailLocked = !isLinkInvite,
  isFullPage = false,
  usersCount,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('models');
  const navigation = useNavigation();
  const { setAuthToken } = useAuthContext();
  const { prompt, userInfo, setUserDetail, clearUserInfo } = useGoogleAuth();
  const apiConfig = getApiConfig();

  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isHoveredGoogle, setIsHoveredGoogle] = useState(false);
  const [isHoveredApple, setIsHoveredApple] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [hasTouchedPassword, setHasTouchedPassword] = useState(false);
  const [isRoleTooltipVisible, setIsRoleTooltipVisible] = useState(false);
  const [isHoveredTerms, setIsHoveredTerms] = useState(false);
  const [isHoveredPrivacyPolicy, setIsHoveredPrivacyPolicy] = useState(false);
  const [signupStep, setSignupStep] = useState<'select' | 'email-form'>(
    'select'
  );
  // TA-11025: the email form's values while its OTP step is showing, and the
  // values + errors to put back if registration bounces the user back to the
  // form (the form unmounts while the code step is up).
  const [pendingEmailSignup, setPendingEmailSignup] =
    useState<ScopedRegisterValues | null>(null);
  const [restoredForm, setRestoredForm] = useState<{
    values: ScopedRegisterValues;
    errors: Partial<Record<keyof ScopedRegisterValues, string>>;
  } | null>(null);
  const isMobile = isMobileBrowser();

  // Clear any stale userInfo from other screens when modal mounts
  useEffect(() => {
    clearUserInfo?.();
  }, []);

  const handleOAuthSubmit = async (oauthUser: {
    email: string;
    given_name?: string;
    family_name?: string;
    id: string;
    provider: string;
    identity_token?: string;
  }) => {
    setSubmitError(null);
    const socialSignInExpired = t('onBoarding.error.socialSignInExpired', {
      provider: oauthUser.provider,
    });
    // TA-11025: the backend rejects social registration without a live,
    // verifiable identity token — don't send one that is bound to fail.
    if (!oauthUser.identity_token) {
      clearUserInfo?.();
      setSubmitError(socialSignInExpired);
      return;
    }
    // Only an unlocked (link-invite) form accepts any address. A locked one —
    // including an expired email invite, which is still in request mode — must
    // match the invited address, or the backend cannot tie the signup to the
    // invitation (TA-10943).
    const isEmailMatch =
      !isEmailLocked || oauthUser.email.toLowerCase() === email.toLowerCase();

    const registerPayload = {
      user: {
        email: oauthUser.email.toLowerCase(),
        password: '',
        terms_of_service: true,
        name: [oauthUser.given_name, oauthUser.family_name]
          .filter(Boolean)
          .join(' '),
        phone_number: '',
        provider: oauthUser.provider,
        uid: oauthUser.id,
        identity_token: oauthUser.identity_token,
        ...(isEmailMatch && { invitation_token: invitationToken }),
        ...(isEmailMatch &&
          isLinkInvite &&
          entityId && { entity_id: entityId }),
      },
    };

    try {
      const response = await fetch(`${apiConfig.apiUrl}/api/v3/users.json`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerPayload),
      });
      const data = await response.json().catch(() => null);
      const result = parseRegisterResponse(response.ok, data, {
        status: response.status,
        rateLimitMessage: t(RATE_LIMIT_ERROR_KEY),
      });

      if (isSocialIdentityError(result)) {
        // The token expired or didn't verify. Drop this sign-in so the
        // provider buttons start a fresh one instead of resending it.
        clearUserInfo?.();
        setSubmitError(socialSignInExpired);
      } else if (result.status === 'error') {
        setSubmitError(result.message ?? t('onBoarding.networkError'));
      } else {
        const token = result.authToken;
        Sentry.setUser({ email: oauthUser.email });
        window.sessionStorage.setItem('setKeepSignedIn', '1');
        if (isEmailMatch) {
          window.sessionStorage.setItem('goto_welcome', '0');
          window.sessionStorage.setItem('invite_signup', '1');
          window.sessionStorage.setItem('invite_signup_type', invitationType);
          // TA-10892 #27: one Start a Conversation tooltip on first Task Details.
          window.localStorage?.setItem(
            INVITE_SIGNUP_START_CONVERSATION_KEY,
            '1'
          );
          // TA-10892 #26: invitees are the only ones who get "You have tasks
          // waiting".
          window.localStorage?.setItem(INVITE_SIGNUP_TASKS_WAITING_KEY, '1');
          if (isLinkInvite) {
            window.sessionStorage.setItem('is_link_invite', '1');
          }
          if (invitationType === 'task') {
            const taskProjectUuid = window.sessionStorage.getItem(
              'invite_task_project_uuid'
            );
            if (taskProjectUuid) {
              window.sessionStorage.setItem(
                'pending_project_uuid',
                taskProjectUuid
              );
              window.sessionStorage.removeItem('invite_task_project_uuid');
            }
            const taskGraphqlId =
              window.sessionStorage.getItem('invite_task_id');
            if (taskGraphqlId) {
              window.sessionStorage.setItem(
                'post_invite_open_task_id',
                taskGraphqlId
              );
              window.sessionStorage.removeItem('invite_task_id');
            }
            // Set before setAuthToken so flags are in sessionStorage when
            // ProjectOnboardingProvider's useRef initializer runs on AppStack mount.
            window.sessionStorage.setItem('skip_project_onboarding', '1');
            window.sessionStorage.setItem('skip_task_tooltip', '1');
          }
        } else {
          // Email mismatch — regular signup, clear invite state so other
          // flows don't accidentally pick up stale invitation context
          // TA-10886: the trial welcome modal was retired — this is a plain
          // signup, not a welcome-modal entry point
          window.sessionStorage.setItem('goto_welcome', '0');
          window.sessionStorage.removeItem('invitation_token');
          window.sessionStorage.removeItem('link_type');
          window.sessionStorage.removeItem('task_or_project_id');
        }
        await SecurityStorage.setItem('authToken', token);
        clearUserInfo?.();
        setAuthToken(token);
        onSuccess();
      }
    } catch {
      setSubmitError(t('onBoarding.networkError'));
    }
  };

  // React to userInfo being set — Google populates it automatically via the
  // hook after prompt(); Apple populates it via handleAppleSuccess → setUserDetail()
  useEffect(() => {
    if (userInfo) {
      handleOAuthSubmit(userInfo);
    }
  }, [userInfo]);

  const handleAppleSuccess = async (response: any) => {
    try {
      const localResponse = await AsyncStorage.getItem('appleUserInfo');
      const userData = localResponse ? JSON.parse(localResponse) : {};

      // TA-11025: only this sign-in's token is proof the backend can verify.
      // The copy cached in AsyncStorage is from an earlier sign-in and long
      // expired, so it is never a fallback — ask for a fresh sign-in instead.
      const idToken: string | undefined = response?.authorization?.id_token;
      if (!idToken) {
        setSubmitError(
          t('onBoarding.error.socialSignInExpired', { provider: 'Apple' })
        );
        return;
      }

      const decoded = jwt_decode<{ sub?: string; email?: string }>(idToken);
      const decodeSub = decoded?.sub ?? '';
      const decodeEmail = decoded?.email ?? '';

      if (!decodeSub) {
        setSubmitError(t('onBoarding.error.appleSignInFailed'));
        return;
      }

      const userInfoPayload = {
        id: decodeSub,
        // The backend requires the registration email to equal the token's
        // (possibly private-relay) email, so the claim wins over any cached
        // or popup-supplied address.
        email: decodeEmail || response?.user?.email || userData?.user?.email,
        given_name:
          response?.user?.name?.firstName || userData?.user?.name?.firstName,
        family_name:
          response?.user?.name?.lastName || userData?.user?.name?.lastName,
        provider: 'Apple',
        identity_token: idToken,
      };

      if (response.user?.email) {
        await AsyncStorage.setItem('appleUserInfo', JSON.stringify(response));
      }
      setUserDetail?.(userInfoPayload);
    } catch {
      setSubmitError(t('onBoarding.networkError'));
    }
  };

  const isLinkInviteTask = isLinkInvite && invitationType === 'task';
  const isLinkInviteTeam = isLinkInvite && invitationType === 'team';

  let subtitleI18nKey = '';
  if (isLinkInviteTeam)
    subtitleI18nKey = 'onBoarding.someoneSharedThisTeamWithYou';
  else if (isLinkInviteTask)
    subtitleI18nKey = 'onBoarding.someoneSharedThisTaskWithYou';
  else if (isLinkInvite)
    subtitleI18nKey = 'onBoarding.youveBeenSharedAProjectOnTaskTag';

  const isEmailTaskInvite = !isLinkInvite && invitationType === 'task';

  const EDITOR_CAPABILITIES = [
    t('onBoarding.editorCapabilities.viewAndManageTasks'),
    t('onBoarding.editorCapabilities.uploadFilesMedia'),
    t('onBoarding.editorCapabilities.collaborateWithTeam'),
    t('onBoarding.editorCapabilities.trackProjectProgress'),
  ];

  const initialValues: ScopedRegisterValues = {
    email: isEmailLocked ? email : '',
    first_name: '',
    last_name: '',
    password: '',
  };
  const isEditorRole = role.toLowerCase() === 'editor';
  const formattedRole = role.charAt(0).toUpperCase() + role.slice(1);
  const startsWithVowel = /^[aeiou]/i.test(formattedRole);

  // TA-11025 (pairs with TA-11024): v3 email/password registration must prove
  // email ownership. Submitting the form only sends the code; registration
  // happens from the code step with `email_verification_code` attached.
  const handleFormSubmit = async (values: ScopedRegisterValues) => {
    setSubmitError(null);
    try {
      const sent = await sendEmailVerificationCode(values.email);
      if (!sent.ok) {
        setSubmitError(
          sent.error ?? t('registerVerifyEmail.unableToSendVerificationCode')
        );
        return;
      }
    } catch {
      setSubmitError(t('onBoarding.networkError'));
      return;
    }
    setRestoredForm(null);
    setPendingEmailSignup(values);
  };

  const backToEmailForm = (
    values: ScopedRegisterValues,
    errors: Partial<Record<keyof ScopedRegisterValues, string>> = {}
  ) => {
    setRestoredForm({ values, errors });
    setPendingEmailSignup(null);
  };

  const registerWithEmail = async (
    values: ScopedRegisterValues,
    emailVerificationCode: string
  ): Promise<string | null> => {
    const registerUrl = `/api/v3/users.json`;
    const registerPayload = {
      user: {
        email: values.email.toLowerCase(),
        password: values.password,
        terms_of_service: true,
        name: [values.first_name, values.last_name]
          .filter((item) => item !== '')
          .join(' '),
        phone_number: '',
        invitation_token: invitationToken,
        email_verification_code: emailVerificationCode,
        ...(isLinkInvite && entityId && { entity_id: entityId }),
      },
    };
    const authEndpoint = `${apiConfig.apiUrl}${registerUrl}`;

    try {
      const response = await fetch(authEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerPayload),
      });
      const data = await response.json().catch(() => null);
      const result = parseRegisterResponse(response.ok, data, {
        status: response.status,
        rateLimitMessage: t(RATE_LIMIT_ERROR_KEY),
      });

      if (result.status === 'error') {
        // A bad/expired code stays on the code step; anything about the
        // email itself belongs on the form the user filled in.
        if (result.fieldErrors.email_verification_code) {
          return result.fieldErrors.email_verification_code;
        }
        if (result.message === 'Email has already been taken') {
          backToEmailForm(values, { email: t('onBoarding.emailAlreadyInUse') });
          return null;
        }
        if (result.fieldErrors.email) {
          backToEmailForm(values, { email: result.fieldErrors.email });
          setSubmitError(result.fieldErrors.email);
          return null;
        }
        return result.message ?? t('onBoarding.networkError');
      } else {
        const token = result.authToken;
        Sentry.setUser({ email: values.email });
        window.sessionStorage.setItem('setKeepSignedIn', '1');
        window.sessionStorage.setItem('goto_welcome', '0');
        window.sessionStorage.setItem('invite_signup', '1');
        window.sessionStorage.setItem('invite_signup_type', invitationType);
        // TA-10892 #27: one Start a Conversation tooltip on first Task Details.
        window.localStorage?.setItem(INVITE_SIGNUP_START_CONVERSATION_KEY, '1');
        // TA-10892 #26: invitees are the only ones who get "You have tasks
        // waiting".
        window.localStorage?.setItem(INVITE_SIGNUP_TASKS_WAITING_KEY, '1');
        if (isLinkInvite) {
          window.sessionStorage.setItem('is_link_invite', '1');
        }
        if (invitationType === 'task') {
          const taskProjectUuid = window.sessionStorage.getItem(
            'invite_task_project_uuid'
          );
          if (taskProjectUuid) {
            window.sessionStorage.setItem(
              'pending_project_uuid',
              taskProjectUuid
            );
            window.sessionStorage.removeItem('invite_task_project_uuid');
          }
          const taskGraphqlId = window.sessionStorage.getItem('invite_task_id');
          if (taskGraphqlId) {
            window.sessionStorage.setItem(
              'post_invite_open_task_id',
              taskGraphqlId
            );
            window.sessionStorage.removeItem('invite_task_id');
          }
          // Set before setAuthToken so flags are in sessionStorage when
          // ProjectOnboardingProvider's useRef initializer runs on AppStack mount.
          window.sessionStorage.setItem('skip_project_onboarding', '1');
          window.sessionStorage.setItem('skip_task_tooltip', '1');
        }
        await SecurityStorage.setItem('authToken', token);
        setAuthToken(token);
        onSuccess();
        return null;
      }
    } catch {
      return t('onBoarding.networkError');
    }
  };

  const emailVerificationContent = pendingEmailSignup && (
    <InviteEmailVerificationStep
      email={pendingEmailSignup.email}
      onSubmitCode={(code) => registerWithEmail(pendingEmailSignup, code)}
      onBack={() => backToEmailForm(pendingEmailSignup)}
    />
  );

  const formContent = (
    <>
      {/* Heading */}
      <Text variant='heading2' textAlign='center' mb='xxs'>
        {t('onBoarding.createAnAccount')}
      </Text>
      <Box flexDirection='row' justifyContent='center' flexWrap='wrap' mb='l'>
        {isLinkInvite ? (
          <Text variant='labelSmall' color='textSecondary'>
            {t(subtitleI18nKey)}
          </Text>
        ) : (
          !!inviterName && (
            <>
              {isEmailTaskInvite ? (
                <>
                  <Text variant='labelSmallBold' color='textPrimary'>
                    {inviterName}
                  </Text>
                  <Text variant='labelSmall' color='textSecondary'>
                    {' '}
                    {t('onBoarding.assignedThisTaskSuffix')}
                  </Text>
                </>
              ) : (
                <>
                  <Text variant='labelSmallBold' color='textPrimary'>
                    {inviterName}
                  </Text>
                  <Text variant='labelSmall' color='textSecondary'>
                    {' '}
                    {t('onBoarding.invitedYouSuffix')}
                  </Text>
                </>
              )}
            </>
          )
        )}
      </Box>

      {/* Context banner */}
      <Box
        backgroundColor={isLinkInvite ? 'blueMild' : 'greenSecondaryMild'}
        borderRadius='s'
        p='m'
        mb='l'
        style={styles.bannerContainer}>
        <Text
          variant={
            invitationType === 'task' && !isLinkInvite
              ? 'labelLargeWeb'
              : 'labelEmphasized'
          }
          color='textPrimary'
          mb='xxs'>
          {entityName}
        </Text>
        {!!entityAddress && invitationType !== 'task' && (
          <Box flexDirection='row' alignItems='center' mb='m'>
            <Icon name='MapPin' variant='s' color='grey04' marginRight='xxs' />
            <Text variant='webBodySecondary' color='grey04'>
              {entityAddress}
            </Text>
          </Box>
        )}
        {!!projectName && invitationType === 'task' && (
          <Box flexDirection='row' alignItems='center' mb='s'>
            <Icon
              name={(projectIcon as keyof typeof Images) || 'BuildingHomeHouse'}
              variant='s'
              color='grey06'
              width={14}
              height={14}
              marginRight='xxs'
            />
            <Text variant='labelSmall' color='textSecondary'>
              {projectName}
            </Text>
          </Box>
        )}
        <Box flexDirection='row' alignItems='center' flexWrap='wrap'>
          {isEmailTaskInvite && (
            <>
              <Text variant='labelSmall' color='textSecondary'>
                {t('onBoarding.youllJoinAsAnPrefix')}
              </Text>
              <Pressable
                {...({
                  onMouseEnter: () => setIsRoleTooltipVisible(true),
                  onMouseLeave: () => setIsRoleTooltipVisible(false),
                } as any)}
                onPress={() => setIsRoleTooltipVisible(!isRoleTooltipVisible)}
                style={
                  {
                    position: 'relative',
                    zIndex: 9999,
                    overflow: 'visible',
                    cursor: 'pointer',
                  } as any
                }>
                <Box
                  borderBottomWidth={1}
                  borderColor='textPrimary'
                  style={{ borderStyle: 'dotted' } as any}>
                  <Text variant='labelSmallBold' color='textPrimary'>
                    {formattedRole}
                  </Text>
                </Box>
                {isRoleTooltipVisible && (
                  <Box style={styles.tooltip}>
                    {EDITOR_CAPABILITIES.map((item, i) => (
                      <Text
                        key={i}
                        variant='metadata'
                        style={styles.tooltipText}>
                        {'\u2022 '}
                        {item}
                      </Text>
                    ))}
                  </Box>
                )}
              </Pressable>
              {usersCount != null && (
                <Text variant='labelSmall' color='textSecondary'>
                  {t('onBoarding.youllJoinAsWithOtherPeople', {
                    count: usersCount,
                  })}
                </Text>
              )}
            </>
          )}
          {isLinkInvite && invitationType === 'project' && (
            <>
              <Text variant='labelSmall' color='textSecondary'>
                {t('onBoarding.yourRole')}
              </Text>
              <Text variant='labelSmallBold' color='textPrimary'>
                {t('onBoarding.viewer')}{' '}
                <Text variant='labelSmall' color='textSecondary'>
                  ({t('onBoarding.pendingApproval')})
                </Text>
              </Text>
            </>
          )}
          {isLinkInvite && invitationType !== 'project' && (
            <>
              <Text variant='labelSmall' color='textSecondary'>
                {t('onBoarding.youllJoinAsAPrefix')}
              </Text>
              <Text variant='labelSmallBold' color='textPrimary'>
                {t('onBoarding.viewer')} ({t('onBoarding.pendingApproval')})
              </Text>
              {usersCount != null && (
                <Text variant='labelSmall' color='textSecondary'>
                  {t('onBoarding.youllJoinAsWithOtherPeople', {
                    count: usersCount,
                  })}
                  .
                </Text>
              )}
            </>
          )}
          {!isLinkInvite && invitationType === 'project' && (
            <>
              <Text variant='webBodySecondary' color='textSecondary'>
                {t('onBoarding.yourRole')}
              </Text>
              <Text
                variant='webSmall'
                color='textSecondary'
                textDecorationLine='underline'>
                {formattedRole}
              </Text>
            </>
          )}
          {!isLinkInvite &&
            !isEmailTaskInvite &&
            invitationType !== 'project' && (
              <>
                <Text variant='labelSmall' color='textSecondary'>
                  {startsWithVowel
                    ? t('onBoarding.youllJoinAsAnPrefix')
                    : t('onBoarding.youllJoinAsAPrefix')}
                </Text>
                {isEditorRole ? (
                  <Pressable
                    {...({
                      onMouseEnter: () => setIsRoleTooltipVisible(true),
                      onMouseLeave: () => setIsRoleTooltipVisible(false),
                    } as any)}
                    onPress={() =>
                      setIsRoleTooltipVisible(!isRoleTooltipVisible)
                    }
                    style={
                      {
                        position: 'relative',
                        zIndex: 9999,
                        overflow: 'visible',
                        cursor: 'pointer',
                      } as any
                    }>
                    <Box
                      borderBottomWidth={1}
                      borderColor='textPrimary'
                      style={{ borderStyle: 'dotted' } as any}>
                      <Text variant='labelSmallBold' color='textPrimary'>
                        {formattedRole}
                      </Text>
                    </Box>
                    {isRoleTooltipVisible && (
                      <Box style={styles.tooltip}>
                        {EDITOR_CAPABILITIES.map((item, i) => (
                          <Text
                            key={i}
                            variant='metadata'
                            style={styles.tooltipText}>
                            {'\u2022 '}
                            {item}
                          </Text>
                        ))}
                      </Box>
                    )}
                  </Pressable>
                ) : (
                  <Text variant='labelSmallBold' color='textPrimary'>
                    {formattedRole}
                  </Text>
                )}
                {usersCount != null && (
                  <Text variant='labelSmall' color='textSecondary'>
                    {t('onBoarding.youllJoinAsSuffix', { count: usersCount })}
                  </Text>
                )}
              </>
            )}
        </Box>
      </Box>

      <Formik
        initialValues={restoredForm?.values ?? initialValues}
        initialErrors={restoredForm?.errors}
        initialTouched={restoredForm ? { email: true } : undefined}
        validationSchema={scopedRegisterSchema}
        validateOnMount={false}
        onSubmit={(values) => handleFormSubmit(values)}>
        {({ handleSubmit: formikSubmit, isSubmitting, values }) => {
          const validLength = values.password.length >= 8;
          const validNumber = /\d/.test(values.password);
          const validUppercase = /[A-Z]/.test(values.password);
          const validSpecial = /[^A-Za-z0-9]/.test(values.password);
          const isFormValid =
            values.first_name.trim() !== '' &&
            values.last_name.trim() !== '' &&
            values.password.trim() !== '' &&
            validLength &&
            validNumber &&
            validUppercase &&
            validSpecial;

          return (
            <Box>
              {/* Google + Apple buttons */}
              <Box flexDirection='row' mb='m'>
                <Box
                  flex={1}
                  mr='xs'
                  borderWidth={1}
                  borderColor='grey02'
                  borderRadius='xs'
                  style={{ overflow: 'hidden' } as any}>
                  <Button
                    onMouseEnter={() => setIsHoveredGoogle(true)}
                    onMouseLeave={() => setIsHoveredGoogle(false)}
                    onPress={() => prompt()}
                    prefix={
                      <Icon
                        variant='l'
                        marginRight='xs'
                        name='Google'
                        color='textPrimary'
                        accessibilityLabel='googleIcon'
                      />
                    }
                    backgroundColor={isHoveredGoogle ? 'grey01' : 'white'}
                    accessibilityLabel={t('onBoarding.google')}
                    fullWidth
                    isSmall>
                    <Text variant='webBodySecondary'>
                      {t('onBoarding.google')}
                    </Text>
                  </Button>
                </Box>
                <Box
                  flex={1}
                  borderWidth={1}
                  borderColor='grey02'
                  borderRadius='xs'
                  style={{ overflow: 'hidden' } as any}>
                  <AppleSignin
                    authOptions={{
                      clientId: process.env.APPLE_CLIENT_ID_WEB,
                      scope: 'name email',
                      redirectURI: process.env.APPLE_REDIRECT_UI,
                      state: '',
                      nonce: 'nonce',
                      usePopup: true,
                    }}
                    render={(props: any) => (
                      <Button
                        onMouseEnter={() => setIsHoveredApple(true)}
                        onMouseLeave={() => setIsHoveredApple(false)}
                        fullWidth
                        isSmall
                        backgroundColor={isHoveredApple ? 'grey01' : 'white'}
                        accessibilityLabel={t('onBoarding.apple')}
                        prefix={
                          <Icon
                            variant='l'
                            name='Apple24'
                            color='textPrimary'
                            accessibilityLabel={t('onBoarding.apple')}
                          />
                        }
                        {...props}>
                        <Text variant='webBodySecondary'>
                          {t('onBoarding.apple')}
                        </Text>
                      </Button>
                    )}
                    uiType='light'
                    className='apple-auth-btn'
                    onSuccess={(response: any) => handleAppleSuccess(response)}
                    onError={(error: any) => {
                      Sentry.captureException(error);
                      setSubmitError(t('onBoarding.networkError'));
                    }}
                  />
                </Box>
              </Box>

              {/* Divider */}
              <Box flexDirection='row' alignItems='center' mb='m'>
                <Box flex={1} height={1} backgroundColor='grey02' />
                <Text variant='metadata' color='textSecondary' mx='s'>
                  {t('onBoarding.orContinueWithEmail')}
                </Text>
                <Box flex={1} height={1} backgroundColor='grey02' />
              </Box>

              {/* Email — read-only when locked to the invited address */}
              <Box mb='l'>
                <Field
                  component={TextField}
                  label={t('shared:email')}
                  name='email'
                  marginBottom='none'
                  placeholder='johndoe@tasktag.com'
                  editable={!isEmailLocked}
                  {...(isEmailLocked && {
                    customBackgroundColor: theme.colors.grey01,
                    customBorderColor: theme.colors.grey02,
                  })}
                  textInputProps={{
                    autoCapitalize: 'none',
                    autoCorrect: false,
                    keyboardType: 'email-address',
                    editable: !isEmailLocked,
                    ...(isEmailLocked && {
                      style: { color: theme.colors.grey04 },
                    }),
                  }}
                  accessibilityLabel='Email address'
                  validateOnChange={false}
                />
              </Box>

              {/* First Name + Last Name side by side */}
              <Box flexDirection='row' mb='s'>
                <Box flex={1} mr='xs'>
                  <Field
                    component={TextField}
                    label={t('shared:firstName')}
                    name='first_name'
                    marginBottom='none'
                    placeholder='e.g. John'
                    accessibilityLabel='First name'
                    validateOnChange={false}
                  />
                </Box>
                <Box flex={1}>
                  <Field
                    component={TextField}
                    label={t('shared:lastName')}
                    name='last_name'
                    marginBottom='none'
                    placeholder='e.g. Doe'
                    accessibilityLabel='Last name'
                    validateOnChange={false}
                  />
                </Box>
              </Box>

              {/* Password with requirements */}
              <Box mb='m'>
                <Field
                  component={TextField}
                  label={t('shared:password')}
                  name='password'
                  marginBottom='none'
                  placeholder={t('onBoarding.enterPassword')}
                  onFocus={() => {
                    setIsPasswordFocused(true);
                    setHasTouchedPassword(true);
                  }}
                  onBlur={() => setIsPasswordFocused(false)}
                  suffix={
                    <Icon
                      name={showPassword ? 'Eye' : 'EyeOff'}
                      variant='l'
                      color='textPrimary'
                      onPress={() => setShowPassword(!showPassword)}
                    />
                  }
                  textInputProps={{
                    autoCapitalize: 'none',
                    autoCorrect: false,
                    secureTextEntry: !showPassword,
                  }}
                  accessibilityLabel='Password'
                  validateOnChange={false}
                />
                {(isPasswordFocused || hasTouchedPassword) && (
                  <Box mt='xs'>
                    <Text variant='metadata' color='textSecondary' mb='xxs'>
                      {t('onBoarding.yourPasswordMustContain')}
                    </Text>
                    {[
                      {
                        label: t('onBoarding.atLeast8Characters'),
                        valid: validLength,
                      },
                      {
                        label: t('onBoarding.atLeast1Number'),
                        valid: validNumber,
                      },
                      {
                        label: t('onBoarding.atLeast1UppcaseLetter'),
                        valid: validUppercase,
                      },
                      {
                        label: t('onBoarding.atLeast1SpecialCharacter'),
                        valid: validSpecial,
                      },
                    ].map((rule, idx) => (
                      <Box
                        key={idx}
                        flexDirection='row'
                        alignItems='center'
                        mb='xxs'>
                        {rule.valid ? (
                          <Icon
                            name='Check'
                            variant='s'
                            color='greenSecondary'
                            marginRight='xxs'
                          />
                        ) : (
                          <Text
                            variant='metadata'
                            color='textSecondary'
                            style={{ marginRight: theme.spacing.xxs }}>
                            {'\u2022'}
                          </Text>
                        )}
                        <Text
                          variant='metadata'
                          color={
                            rule.valid ? 'greenSecondary' : 'textSecondary'
                          }>
                          {rule.label}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {!!submitError && (
                <Text
                  variant='metadata'
                  color='alertRed'
                  mb='m'
                  textAlign='center'>
                  {submitError}
                </Text>
              )}

              {/* Terms */}
              <Box mb='m'>
                <Text
                  variant='metadataSecondaryNew'
                  color='textSecondary'
                  style={{ lineHeight: 16 }}>
                  {t('onBoarding.bySigningUpIAgreeToTheTaskTag')}
                  <Text
                    variant='metadataSecondaryNew'
                    color='greenSecondary'
                    textDecorationLine={
                      isHoveredTerms ? 'underline' : undefined
                    }
                    onMouseEnter={() => setIsHoveredTerms(true)}
                    onMouseLeave={() => setIsHoveredTerms(false)}
                    onPress={async () => {
                      await Linking.openURL(
                        'https://www.tasktag.com/terms-and-conditions'
                      );
                    }}>
                    {t('onBoarding.termsAndConditions')}
                  </Text>
                  {t('onBoarding.and')}
                  <Text
                    variant='metadataSecondaryNew'
                    color='greenSecondary'
                    textDecorationLine={
                      isHoveredPrivacyPolicy ? 'underline' : undefined
                    }
                    onMouseEnter={() => setIsHoveredPrivacyPolicy(true)}
                    onMouseLeave={() => setIsHoveredPrivacyPolicy(false)}
                    onPress={async () => {
                      await Linking.openURL('https://www.tasktag.com/privacy');
                    }}>
                    {t('onBoarding.privacyPolicy')}
                  </Text>
                  {t('onBoarding.dot')}
                </Text>
              </Box>

              {/* Submit Button */}
              <Button
                variant='webprimary'
                fullWidth
                isSmall
                disabled={isSubmitting || !isFormValid}
                onPress={() => formikSubmit()}
                accessibilityLabel={
                  isLinkInvite
                    ? t('onBoarding.requestToJoin')
                    : t('onBoarding.joinThis', {
                        entityType:
                          invitationType.charAt(0).toUpperCase() +
                          invitationType.slice(1),
                      })
                }>
                {isLinkInvite
                  ? t('onBoarding.requestToJoin')
                  : t('onBoarding.joinThis', {
                      entityType:
                        invitationType.charAt(0).toUpperCase() +
                        invitationType.slice(1),
                    })}
              </Button>

              {/* Log in link */}
              <Box
                flexDirection='row'
                alignItems='center'
                justifyContent='center'
                mt='m'>
                <Text variant='metadata' color='textPrimary'>
                  {t('onBoarding.alreadyHaveAnAccount')}
                </Text>
                <Text
                  variant='metadataSemiBold'
                  color='greenSecondary'
                  accessibilityLabel={t('onBoarding.logIn')}
                  onPress={() => {
                    onClose();
                    (navigation as any).navigate('login');
                  }}>
                  {t('onBoarding.logIn')}
                </Text>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </>
  );

  const mobileSelectContent = (
    <>
      <Text variant='heading2' textAlign='center' mb='xxs'>
        {t('onBoarding.createAnAccount')}
      </Text>
      <Box flexDirection='row' justifyContent='center' flexWrap='wrap' mb='l'>
        {isLinkInvite ? (
          <Text variant='labelSmall' color='textSecondary'>
            {t(subtitleI18nKey)}
          </Text>
        ) : (
          !!inviterName && (
            <>
              {isEmailTaskInvite ? (
                <>
                  <Text variant='labelSmallBold' color='textPrimary'>
                    {inviterName}
                  </Text>
                  <Text variant='labelSmall' color='textSecondary'>
                    {' '}
                    {t('onBoarding.assignedThisTaskSuffix')}
                  </Text>
                </>
              ) : (
                <>
                  <Text variant='labelSmallBold' color='textPrimary'>
                    {inviterName}
                  </Text>
                  <Text variant='labelSmall' color='textSecondary'>
                    {' '}
                    {t('onBoarding.invitedYouSuffix')}
                  </Text>
                </>
              )}
            </>
          )
        )}
      </Box>

      {/* Context banner */}
      <Box
        backgroundColor={isLinkInvite ? 'blueMild' : 'greenSecondaryMild'}
        borderRadius='s'
        p='m'
        mb='l'
        style={styles.bannerContainer}>
        <Text
          variant={
            invitationType === 'task' && !isLinkInvite
              ? 'labelLargeWeb'
              : 'labelEmphasized'
          }
          color='textPrimary'
          style={{ marginBottom: 2 } as any}>
          {entityName}
        </Text>
        {!!entityAddress && invitationType !== 'task' && (
          <Box flexDirection='row' alignItems='center' mb='m'>
            <Icon name='MapPin' variant='s' color='grey04' marginRight='xxs' />
            <Text variant='webBodySecondary' color='grey04'>
              {entityAddress}
            </Text>
          </Box>
        )}
        {!!projectName && invitationType === 'task' && (
          <Box flexDirection='row' alignItems='center' mb='s'>
            <Icon
              name={(projectIcon as keyof typeof Images) || 'BuildingHomeHouse'}
              variant='s'
              color='grey06'
              width={14}
              height={14}
              marginRight='xxs'
            />
            <Text variant='labelSmall' color='textSecondary'>
              {projectName}
            </Text>
          </Box>
        )}
        <Box flexDirection='row' alignItems='center' flexWrap='wrap'>
          {isEmailTaskInvite && (
            <>
              <Text variant='labelSmall' color='textSecondary'>
                {t('onBoarding.youllJoinAsAnPrefix')}
              </Text>
              <Pressable
                {...({
                  onMouseEnter: () => setIsRoleTooltipVisible(true),
                  onMouseLeave: () => setIsRoleTooltipVisible(false),
                } as any)}
                onPress={() => setIsRoleTooltipVisible(!isRoleTooltipVisible)}
                style={
                  {
                    position: 'relative',
                    zIndex: 9999,
                    overflow: 'visible',
                    cursor: 'pointer',
                  } as any
                }>
                <Box
                  borderBottomWidth={1}
                  borderColor='textPrimary'
                  style={{ borderStyle: 'dotted' } as any}>
                  <Text variant='labelSmallBold' color='textPrimary'>
                    {formattedRole}
                  </Text>
                </Box>
                {isRoleTooltipVisible && (
                  <Box style={styles.tooltip}>
                    {EDITOR_CAPABILITIES.map((item, i) => (
                      <Text
                        key={i}
                        variant='metadata'
                        style={styles.tooltipText}>
                        {'\u2022 '}
                        {item}
                      </Text>
                    ))}
                  </Box>
                )}
              </Pressable>
              {usersCount != null && (
                <Text variant='labelSmall' color='textSecondary'>
                  {t('onBoarding.youllJoinAsWithOtherPeople', {
                    count: usersCount,
                  })}
                </Text>
              )}
            </>
          )}
          {isLinkInvite && invitationType === 'project' && (
            <>
              <Text variant='labelSmall' color='textSecondary'>
                {t('onBoarding.yourRole')}
              </Text>
              <Text variant='labelSmallBold' color='textPrimary'>
                {t('onBoarding.viewer')}{' '}
                <Text variant='labelSmall' color='textSecondary'>
                  ({t('onBoarding.pendingApproval')})
                </Text>
              </Text>
            </>
          )}
          {isLinkInvite && invitationType !== 'project' && (
            <>
              <Text variant='labelSmall' color='textSecondary'>
                {t('onBoarding.youllJoinAsAPrefix')}
              </Text>
              <Text variant='labelSmallBold' color='textPrimary'>
                {t('onBoarding.viewer')} ({t('onBoarding.pendingApproval')})
              </Text>
              {usersCount != null && (
                <Text variant='labelSmall' color='textSecondary'>
                  {t('onBoarding.youllJoinAsWithOtherPeople', {
                    count: usersCount,
                  })}
                  .
                </Text>
              )}
            </>
          )}
          {!isLinkInvite && invitationType === 'project' && (
            <>
              <Text variant='webBodySecondary' color='textSecondary'>
                {t('onBoarding.yourRole')}
              </Text>
              <Text
                variant='webSmall'
                color='textSecondary'
                textDecorationLine='underline'>
                {formattedRole}
              </Text>
            </>
          )}
          {!isLinkInvite &&
            !isEmailTaskInvite &&
            invitationType !== 'project' && (
              <>
                <Text variant='labelSmall' color='textSecondary'>
                  {startsWithVowel
                    ? t('onBoarding.youllJoinAsAnPrefix')
                    : t('onBoarding.youllJoinAsAPrefix')}
                </Text>
                {isEditorRole ? (
                  <Pressable
                    {...({
                      onMouseEnter: () => setIsRoleTooltipVisible(true),
                      onMouseLeave: () => setIsRoleTooltipVisible(false),
                    } as any)}
                    onPress={() =>
                      setIsRoleTooltipVisible(!isRoleTooltipVisible)
                    }
                    style={
                      {
                        position: 'relative',
                        zIndex: 9999,
                        overflow: 'visible',
                        cursor: 'pointer',
                      } as any
                    }>
                    <Box
                      borderBottomWidth={1}
                      borderColor='textPrimary'
                      style={{ borderStyle: 'dotted' } as any}>
                      <Text variant='labelSmallBold' color='textPrimary'>
                        {formattedRole}
                      </Text>
                    </Box>
                    {isRoleTooltipVisible && (
                      <Box style={styles.tooltip}>
                        {EDITOR_CAPABILITIES.map((item, i) => (
                          <Text
                            key={i}
                            variant='metadata'
                            style={styles.tooltipText}>
                            {'\u2022 '}
                            {item}
                          </Text>
                        ))}
                      </Box>
                    )}
                  </Pressable>
                ) : (
                  <Text variant='labelSmallBold' color='textPrimary'>
                    {formattedRole}
                  </Text>
                )}
                {usersCount != null && (
                  <Text variant='labelSmall' color='textSecondary'>
                    {t('onBoarding.youllJoinAsSuffix', { count: usersCount })}
                  </Text>
                )}
              </>
            )}
        </Box>
      </Box>

      {/* Social sign-in */}
      <Box mb='l' alignItems='center'>
        <Box flexDirection='row' width='100%' mb='l'>
          <Box
            flex={1}
            mr='xs'
            borderWidth={1}
            borderColor='grey02'
            borderRadius='xs'
            style={{ overflow: 'hidden' } as any}>
            <Button
              onMouseEnter={() => setIsHoveredGoogle(true)}
              onMouseLeave={() => setIsHoveredGoogle(false)}
              onPress={() => prompt()}
              prefix={
                <Icon
                  variant='l'
                  marginRight='xs'
                  name='Google'
                  color='textPrimary'
                  accessibilityLabel='googleIcon'
                />
              }
              backgroundColor={isHoveredGoogle ? 'grey01' : 'white'}
              accessibilityLabel={t('onBoarding.google')}
              fullWidth
              isSmall>
              <Text variant='webBodySecondary'>{t('onBoarding.google')}</Text>
            </Button>
          </Box>
          <Box
            flex={1}
            borderWidth={1}
            borderColor='grey02'
            borderRadius='xs'
            style={{ overflow: 'hidden' } as any}>
            <AppleSignin
              authOptions={{
                clientId: process.env.APPLE_CLIENT_ID_WEB,
                scope: 'name email',
                redirectURI: process.env.APPLE_REDIRECT_UI,
                state: '',
                nonce: 'nonce',
                usePopup: true,
              }}
              render={(props: any) => (
                <Button
                  onMouseEnter={() => setIsHoveredApple(true)}
                  onMouseLeave={() => setIsHoveredApple(false)}
                  fullWidth
                  isSmall
                  backgroundColor={isHoveredApple ? 'grey01' : 'white'}
                  accessibilityLabel={t('onBoarding.apple')}
                  prefix={
                    <Icon
                      variant='l'
                      name='Apple24'
                      color='textPrimary'
                      accessibilityLabel={t('onBoarding.apple')}
                    />
                  }
                  {...props}>
                  <Text variant='webBodySecondary'>
                    {t('onBoarding.apple')}
                  </Text>
                </Button>
              )}
              uiType='light'
              className='apple-auth-btn'
              onSuccess={(response: any) => handleAppleSuccess(response)}
              onError={(error: any) => {
                Sentry.captureException(error);
                setSubmitError(t('onBoarding.networkError'));
              }}
            />
          </Box>
        </Box>

        <Box
          flexDirection='row'
          alignItems='center'
          width='100%'
          style={{ gap: theme.spacing.s } as any}>
          <Box flex={1} height={1} backgroundColor='grey02' />
          <Text variant='metadata' color='textSecondary'>
            {t('onBoarding.or')}
          </Text>
          <Box flex={1} height={1} backgroundColor='grey02' />
        </Box>
      </Box>

      {/* Sign up with email CTA */}
      <Pressable
        onPress={() => setSignupStep('email-form')}
        style={({ pressed }) =>
          [
            {
              width: '100%',
              height: 44,
              borderRadius: theme.borderRadii.xs,
              borderWidth: 1,
              borderColor: theme.colors.greenSecondary,
              backgroundColor: pressed
                ? theme.colors.greenSecondaryMild
                : theme.colors.white,
              alignItems: 'center',
              justifyContent: 'center',
            },
          ] as any
        }>
        <Text variant='webBodySecondary' color='greenSecondary'>
          {t('onBoarding.modal.signUpWithEmail')}
        </Text>
      </Pressable>

      {/* Already have an account */}
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='center'
        mt='l'>
        <Text variant='metadata' color='textPrimary'>
          {t('onBoarding.alreadyHaveAnAccount')}
        </Text>
        <Text
          variant='metadataSemiBold'
          color='greenSecondary'
          accessibilityLabel={t('onBoarding.logIn')}
          onPress={() => {
            onClose();
            (navigation as any).navigate('login');
          }}>
          {t('onBoarding.logIn')}
        </Text>
      </Box>
    </>
  );

  const mobileEmailFormContent = (
    <>
      {/* Title bar */}
      <Box flexDirection='row' alignItems='center' mb='l'>
        <Pressable
          onPress={() => setSignupStep('select')}
          hitSlop={8}
          style={styles.backButton}>
          <Icon
            name='ChevronLeft'
            variant='s'
            color='textPrimary'
            accessibilityLabel='Back'
          />
        </Pressable>
        <Box flex={1} alignItems='center'>
          <Text variant='heading2' textAlign='center'>
            {t('onBoarding.modal.signUpWithEmail')}
          </Text>
        </Box>
        <Box style={{ width: 32 } as any} />
      </Box>

      <Formik
        initialValues={restoredForm?.values ?? initialValues}
        initialErrors={restoredForm?.errors}
        initialTouched={restoredForm ? { email: true } : undefined}
        validationSchema={scopedRegisterSchema}
        validateOnMount={false}
        onSubmit={(values) => handleFormSubmit(values)}>
        {({ handleSubmit: formikSubmit, isSubmitting, values }) => {
          const validLength = values.password.length >= 8;
          const validNumber = /\d/.test(values.password);
          const validUppercase = /[A-Z]/.test(values.password);
          const validSpecial = /[^A-Za-z0-9]/.test(values.password);
          const isFormValid =
            values.first_name.trim() !== '' &&
            values.last_name.trim() !== '' &&
            values.password.trim() !== '' &&
            validLength &&
            validNumber &&
            validUppercase &&
            validSpecial;

          return (
            <Box>
              {/* Email */}
              <Box mb='l'>
                <Field
                  component={TextField}
                  label={t('shared:email')}
                  name='email'
                  marginBottom='none'
                  placeholder='johndoe@tasktag.com'
                  editable={!isEmailLocked}
                  {...(isEmailLocked && {
                    customBackgroundColor: theme.colors.grey01,
                    customBorderColor: theme.colors.grey02,
                  })}
                  textInputProps={{
                    autoCapitalize: 'none',
                    autoCorrect: false,
                    keyboardType: 'email-address',
                    editable: !isEmailLocked,
                    ...(isEmailLocked && {
                      style: { color: theme.colors.grey04 },
                    }),
                  }}
                  accessibilityLabel='Email address'
                  validateOnChange={false}
                />
              </Box>

              {/* First Name + Last Name */}
              <Box
                flexDirection='row'
                mb='s'
                style={{ gap: theme.spacing.s } as any}>
                <Box flex={1}>
                  <Field
                    component={TextField}
                    label={t('shared:firstName')}
                    name='first_name'
                    marginBottom='none'
                    placeholder='e.g. John'
                    accessibilityLabel='First name'
                    validateOnChange={false}
                  />
                </Box>
                <Box flex={1}>
                  <Field
                    component={TextField}
                    label={t('shared:lastName')}
                    name='last_name'
                    marginBottom='none'
                    placeholder='e.g. Doe'
                    accessibilityLabel='Last name'
                    validateOnChange={false}
                  />
                </Box>
              </Box>

              {/* Password */}
              <Box mb='m'>
                <Field
                  component={TextField}
                  label={t('shared:password')}
                  name='password'
                  marginBottom='none'
                  placeholder={t('onBoarding.enterPassword')}
                  onFocus={() => {
                    setIsPasswordFocused(true);
                    setHasTouchedPassword(true);
                  }}
                  onBlur={() => setIsPasswordFocused(false)}
                  suffix={
                    <Icon
                      name={showPassword ? 'Eye' : 'EyeOff'}
                      variant='l'
                      color='textPrimary'
                      onPress={() => setShowPassword(!showPassword)}
                    />
                  }
                  textInputProps={{
                    autoCapitalize: 'none',
                    autoCorrect: false,
                    secureTextEntry: !showPassword,
                  }}
                  accessibilityLabel='Password'
                  validateOnChange={false}
                />
                {(isPasswordFocused || hasTouchedPassword) && (
                  <Box mt='xs'>
                    <Text variant='metadata' color='textSecondary' mb='xxs'>
                      {t('onBoarding.yourPasswordMustContain')}
                    </Text>
                    {[
                      {
                        label: t('onBoarding.atLeast8Characters'),
                        valid: validLength,
                      },
                      {
                        label: t('onBoarding.atLeast1Number'),
                        valid: validNumber,
                      },
                      {
                        label: t('onBoarding.atLeast1UppcaseLetter'),
                        valid: validUppercase,
                      },
                      {
                        label: t('onBoarding.atLeast1SpecialCharacter'),
                        valid: validSpecial,
                      },
                    ].map((rule, idx) => (
                      <Box
                        key={idx}
                        flexDirection='row'
                        alignItems='center'
                        mb='xxs'>
                        {rule.valid ? (
                          <Icon
                            name='Check'
                            variant='s'
                            color='greenSecondary'
                            marginRight='xxs'
                          />
                        ) : (
                          <Text
                            variant='metadata'
                            color='textSecondary'
                            style={{ marginRight: theme.spacing.xxs }}>
                            {'\u2022'}
                          </Text>
                        )}
                        <Text
                          variant='metadata'
                          color={
                            rule.valid ? 'greenSecondary' : 'textSecondary'
                          }>
                          {rule.label}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {!!submitError && (
                <Text
                  variant='metadata'
                  color='alertRed'
                  mb='m'
                  textAlign='center'>
                  {submitError}
                </Text>
              )}

              {/* Terms */}
              <Box mb='m'>
                <Text
                  variant='metadataSecondaryNew'
                  color='textSecondary'
                  style={{ lineHeight: 16 }}>
                  {t('onBoarding.bySigningUpIAgreeToTheTaskTag')}
                  <Text
                    variant='metadataSecondaryNew'
                    color='greenSecondary'
                    textDecorationLine={
                      isHoveredTerms ? 'underline' : undefined
                    }
                    onMouseEnter={() => setIsHoveredTerms(true)}
                    onMouseLeave={() => setIsHoveredTerms(false)}
                    onPress={async () => {
                      await Linking.openURL(
                        'https://www.tasktag.com/terms-and-conditions'
                      );
                    }}>
                    {t('onBoarding.termsAndConditions')}
                  </Text>
                  {t('onBoarding.and')}
                  <Text
                    variant='metadataSecondaryNew'
                    color='greenSecondary'
                    textDecorationLine={
                      isHoveredPrivacyPolicy ? 'underline' : undefined
                    }
                    onMouseEnter={() => setIsHoveredPrivacyPolicy(true)}
                    onMouseLeave={() => setIsHoveredPrivacyPolicy(false)}
                    onPress={async () => {
                      await Linking.openURL('https://www.tasktag.com/privacy');
                    }}>
                    {t('onBoarding.privacyPolicy')}
                  </Text>
                  {t('onBoarding.dot')}
                </Text>
              </Box>

              {/* Submit */}
              <Button
                variant='webprimary'
                fullWidth
                isSmall
                disabled={isSubmitting || !isFormValid}
                onPress={() => formikSubmit()}
                accessibilityLabel={
                  isLinkInvite
                    ? t('onBoarding.requestToJoin')
                    : t('onBoarding.joinThis', {
                        entityType:
                          invitationType.charAt(0).toUpperCase() +
                          invitationType.slice(1),
                      })
                }>
                {isLinkInvite
                  ? t('onBoarding.requestToJoin')
                  : t('onBoarding.joinThis', {
                      entityType:
                        invitationType.charAt(0).toUpperCase() +
                        invitationType.slice(1),
                    })}
              </Button>
            </Box>
          );
        }}
      </Formik>
    </>
  );

  if (isFullPage) {
    return (
      <ScrollView
        style={styles.fullPageScroll}
        contentContainerStyle={styles.fullPageContent}
        showsVerticalScrollIndicator={false}>
        <Box style={styles.fullPageForm}>
          <Box alignItems='center' mb='l'>
            <Icon name='Logo' width={120} height={34} color='textPrimary' />
          </Box>
          {emailVerificationContent || formContent}
        </Box>
      </ScrollView>
    );
  }

  return (
    <Modal animationType='fade' transparent={true} visible={true}>
      <Box style={styles.modalContainer}>
        <TouchableWithoutFeedback onPress={onClose}>
          <Box style={styles.backdrop} />
        </TouchableWithoutFeedback>
        <ShadowBox
          variant='base'
          borderRadius={isMobile ? 'l' : 'm'}
          backgroundColor='white'
          style={[
            styles.popUp,
            isMobile && styles.popUpMobile,
            (isPasswordFocused || hasTouchedPassword) && { maxHeight: '100%' },
          ]}>
          {/* Close button */}
          <Box
            style={[styles.closeButton, isMobile && styles.closeButtonMobile]}>
            <Icon
              name='X'
              variant={isMobile ? 's' : 'm'}
              color={isMobile ? 'textPrimary' : 'grey05'}
              onPress={onClose}
              accessibilityLabel='Close'
            />
          </Box>

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[
              styles.scrollContent,
              isMobile &&
                signupStep === 'select' &&
                styles.scrollContentMobileSelect,
              isMobile &&
                signupStep === 'email-form' &&
                styles.scrollContentMobileEmailForm,
            ]}>
            {(() => {
              if (emailVerificationContent) return emailVerificationContent;
              if (isMobile && signupStep === 'select')
                return mobileSelectContent;
              if (isMobile && signupStep === 'email-form')
                return mobileEmailFormContent;
              return formContent;
            })()}
          </ScrollView>
        </ShadowBox>
      </Box>
    </Modal>
  );
};

const styles = StyleSheet.create({
  fullPageScroll: {
    flex: 1,
    backgroundColor: theme.colors.white,
  },
  fullPageContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.l,
  },
  fullPageForm: {
    width: '100%' as any,
    maxWidth: 480,
  },
  modalContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.colors.black,
    opacity: 0.5,
  },
  popUp: {
    position: 'absolute',
    left: 0,
    right: 0,
    marginLeft: 'auto' as any,
    marginRight: 'auto' as any,
    maxWidth: 560,
    maxHeight: '90%',
  },
  popUpMobile: {
    left: theme.spacing.s,
    right: theme.spacing.s,
    marginLeft: 0,
    marginRight: 0,
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing.m,
    right: theme.spacing.m,
    zIndex: 10,
  },
  closeButtonMobile: {
    width: 32,
    height: 32,
    borderRadius: theme.borderRadii.m,
    backgroundColor: theme.colors.grey01,
    alignItems: 'center' as any,
    justifyContent: 'center' as any,
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: theme.borderRadii.xs,
    backgroundColor: theme.colors.grey01,
    alignItems: 'center' as any,
    justifyContent: 'center' as any,
  },
  scrollContent: {
    padding: theme.spacing.l,
  },
  scrollContentMobileSelect: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.xxl + theme.spacing.xs,
    paddingBottom: theme.spacing.l,
  },
  scrollContentMobileEmailForm: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.xxl + theme.spacing.xs,
    paddingBottom: theme.spacing.m,
  },
  bannerContainer: {
    zIndex: 10,
    overflow: 'visible',
  } as any,
  tooltip: {
    position: 'absolute',
    top: 24,
    left: 0,
    backgroundColor: theme.colors.textPrimary,
    borderRadius: theme.borderRadii.xs,
    paddingHorizontal: theme.spacing.s,
    paddingVertical: theme.spacing.xs,
    zIndex: 9999,
    minWidth: 200,
  } as any,
  tooltipText: {
    color: theme.colors.white,
    lineHeight: 22,
  },
});
